import {
  DiscountSettings,
  DiscountSettingsType,
} from "@redotech/redo-model/discount/discount-db-parser";
import { ProductImageAspectRatio } from "@redotech/redo-model/image";
import { ButtonSettings } from "@redotech/redo-model/order-tracking/tracking-page-definition";
import { RedoRecommendedProduct } from "@redotech/redo-model/redo-recommended-product-schema";
import classNames from "classnames";
import { memo, useContext, useEffect, useState } from "react";
import { RedoButton } from "../../../arbiter-components/buttons/redo-button";
import { RedoSingleSelectDropdownInput } from "../../../arbiter-components/select-dropdown/redo-single-select-dropdown-input";
import { CurrencyContext } from "../../../currency";
import { Flex } from "../../../flex";
import { Text } from "../../../text";
import { TrackingPageContext } from "../../context/tracking-page-context";
import {
  getBodyStyles,
  getButtonBorderRadius,
  getButtonHierarchy,
  getButtonStyles,
} from "../shared/block-styles";
import { calculateDiscountPrice } from "./discount-util";
import * as trackingCss from "./product-upsell.module.css";
import { UpsellRedirectHandler } from "./upsell-redirect";

export const RecommendedProductComponent = memo(
  function RecommendedProductComponent({
    discountSettings,
    product,
    index,
    upsellRedirect,
    imageAspectRatio,
    checkoutButtonSettings,
    dropdownSettings,
  }: {
    product: RedoRecommendedProduct;
    index: number;
    discountSettings?: DiscountSettings;
    upsellRedirect?: UpsellRedirectHandler;
    imageAspectRatio?: number;
    checkoutButtonSettings?: ButtonSettings;
    dropdownSettings?: ButtonSettings;
  }) {
    const { formatCurrency } = useContext(CurrencyContext);
    const trackingPageContext = useContext(TrackingPageContext);
    const [isHovered, setIsHovered] = useState(false);

    const bodyStyles = getBodyStyles(trackingPageContext?.pageSettings);
    const checkoutButtonStyles = getButtonStyles(
      trackingPageContext?.pageSettings?.pageButtonSettings,
      checkoutButtonSettings,
    );
    const buttonHierarchy = getButtonHierarchy(checkoutButtonSettings);
    const dropdownStyles = getButtonStyles(
      trackingPageContext?.pageSettings?.pageButtonSettings,
      dropdownSettings,
    );
    const buttonBorderRadius = getButtonBorderRadius(
      trackingPageContext?.pageSettings,
    );
    const bodyFontFamily =
      trackingPageContext?.pageSettings?.typography.bodyFontFamily;

    const getImageUrl = (product: RedoRecommendedProduct) => {
      const imageUrl =
        isHovered && product?.altImage?.url && product?.altImage?.url
          ? new URL(product?.altImage?.url)
          : product?.featuredImage?.url
            ? new URL(product?.featuredImage?.url)
            : null;
      if (!imageUrl) {
        return null;
      }

      imageUrl.searchParams.append("width", "144");
      return imageUrl.toString();
    };
    const getAltText = (product: RedoRecommendedProduct) => {
      return isHovered && product?.altImage?.altText
        ? product?.altImage?.altText
        : product?.featuredImage?.altText;
    };

    const options = product.options.reduce(
      (acc, option) => {
        // Products with no variants have a "Title" option, we want to skip these
        if (option.name === "Title") {
          return acc;
        }

        acc[option.name] = option.values;
        return acc;
      },
      {} as Record<string, string[]>,
    );

    const [selectedOptions, setSelectedOptions] = useState<
      { name: string; value: string }[] | undefined
    >(undefined);

    // select the default variant
    useEffect(() => {
      setSelectedOptions(
        // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
        product.options.map((option) => ({
          name: option.name,
          value: option.values[0],
        })),
      );
    }, [product.options]);

    const variants = product.variants.nodes.map((variant) => variant);

    const selectedVariant = variants.find((variant) => {
      return selectedOptions?.every((selectedOption) =>
        variant.selectedOptions.some(
          (option) =>
            option.name === selectedOption.name &&
            option.value === selectedOption.value,
        ),
      );
    });

    const selectedOption = (option: string) =>
      selectedOptions?.find((selectedOption) => selectedOption.name === option);

    return (
      <div aria-label="Upsell product">
        <Flex align="center" className={trackingCss.product} key={index}>
          <img
            alt={getAltText(product) ?? undefined}
            className={trackingCss.productImage}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
            src={getImageUrl(product) ?? undefined}
            style={{
              aspectRatio: imageAspectRatio ?? ProductImageAspectRatio.SQUARE,
              height: "auto",
            }}
          />
          <Flex
            className={trackingCss.productInfo}
            dir="row"
            grow={1}
            justify="space-between"
          >
            <Flex
              className={trackingCss.productDetails}
              dir="column"
              flex={1}
              gap="xs"
            >
              <Text
                className={classNames(trackingCss.productTitle)}
                fontSize="xs"
                fontWeight="thin"
                style={bodyStyles}
                textColor="secondary"
              >
                {product.title}
              </Text>
              <span aria-label="Product price">
                {getPriceDetails(product, formatCurrency, discountSettings)}
              </span>
              {Object.keys(options).map((option, index) => (
                <Flex key={index}>
                  <RedoSingleSelectDropdownInput
                    dangerousStyleThatShouldOnlyBeUsedForMerchantBranding={{
                      ...dropdownStyles,
                      fontFamily: bodyFontFamily,
                    }}
                    fitToAnchor={false}
                    flexGrow={false}
                    // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
                    options={options[option].map((option) => ({
                      value: option,
                      id: option,
                      type: "text",
                      text: option,
                    }))}
                    optionSelected={({ item: selectedOption }) => {
                      setSelectedOptions((prevOptions) => {
                        const existingOption = prevOptions?.find(
                          (prevOption) => prevOption.name === option,
                        );
                        if (existingOption) {
                          return prevOptions?.map((prevOption) =>
                            prevOption.name === option
                              ? { name: option, value: selectedOption.value }
                              : prevOption,
                          );
                        }
                        return [
                          ...(prevOptions || []),
                          { name: option, value: selectedOption.value },
                        ];
                      });
                    }}
                    selectedItem={
                      selectedOption(option)
                        ? {
                            value: selectedOption(option)?.value ?? "",
                            id: selectedOption(option)?.value ?? "",
                            type: "text",
                            text: selectedOption(option)?.value ?? "",
                          }
                        : {
                            value: option,
                            id: option,
                            type: "text",
                            text: option,
                          }
                    }
                    size="xs"
                  />
                </Flex>
              ))}
            </Flex>
            <Flex
              align="center"
              className={trackingCss.addToCartWrapper}
              justify="center"
            >
              <a
                onClick={() =>
                  upsellRedirect?.logUpsellAndRedirect({
                    id: selectedVariant?.id?.split("/").pop() ?? "",
                    handle: product.handle,
                    productUrl: product.onlineStoreUrl,
                  })
                }
                rel="noreferrer"
                target="_blank"
              >
                <RedoButton
                  dangerousStyleThatShouldOnlyBeUsedForMerchantBranding={{
                    ...checkoutButtonStyles,
                    ...buttonBorderRadius,
                  }}
                  disabled={!selectedVariant}
                  hierarchy={buttonHierarchy}
                  size="sm"
                  text="Add to cart"
                  theme="normal"
                />
              </a>
            </Flex>
          </Flex>
        </Flex>
      </div>
    );
  },
);

const getPriceDetails = (
  product: RedoRecommendedProduct,
  formatCurrency: (amount: number) => string,
  discountSettings?: DiscountSettings,
) => {
  // Only BASIC discounts support product price calculations
  if (
    !discountSettings ||
    discountSettings.settingsType !== DiscountSettingsType.SHOPIFY_BASIC
  ) {
    return <Text fontWeight="semibold">{formatCurrency(product.price)}</Text>;
  }

  const { price, discounted } = calculateDiscountPrice(
    product,
    discountSettings,
  );
  return (
    <span>
      {discounted && (
        <span className={classNames(trackingCss.strikethrough)}>
          {formatCurrency(product.price)}
        </span>
      )}
      <Text fontWeight="semibold">{formatCurrency(price)}</Text>
    </span>
  );
};
